import { useMemo, useRef, useState } from 'react'
import type { RefObject } from 'react'
import * as THREE from 'three'
import { useFrame } from '@react-three/fiber'
import { TMatic } from './TMatic'
import { KMatic, K_VORK } from './KMatic'
import { LMatic } from './LMatic'
import { RMatic, R_DRAAIPUNT, R_MAST, R_VORK } from './RMatic'
import type { TruckKleuren } from './onderdelen'
import { bezetting, maakCyclus, moment, palletOpVork, palletY } from './werkcyclus'
import { HEEN_EN_WEER, WERKTUIG } from './tempo'
import type { MaticModel } from './tempo'
import { DWARSPLANK, maakHeenEnWeer, maakVasteRit, momentHeenEnWeer } from './heenEnWeer'
import { LindeBeeld, Volgorde, useLindeBeeld } from './LindeBeeld'
import { LINDE_BEELD } from './lindeKaarten'
import { OMLOOP, halklok } from '../omloop'
import { Overslagstation, Vloerkader } from './Overslag'
import { PalletMetLading } from '../PalletMetLading'
import { Contactschaduw } from './Contactschaduw'
import type { Soort } from '../PalletMetLading'
import { STELLING_OFFSET } from '../indeling'
import type { Werkpunten } from '../werkgangen'
import { useAnimatie, useMaterialen } from '../../scene/store'
import type { MerkTokens } from '../../kiosk/useMerkTokens'

export type TruckModel = MaticModel

/**
 * Zwaailicht en Bluespot per model. Het zwaailicht staat waar de machine het
 * zelf heeft (T-Matic: op de sensorzuil, K-Matic: boven op de lidarbeugel van
 * de Balyo-module), de Bluespot net voor de vorkpunten — en die liggen bij de
 * K-Matic, met zijn cabine en draaikop vóór de mast, ruim een meter verder.
 *
 * De posities zijn de voet van de lamp. Bij de R-MATIC stond hij in de
 * Balyo-kast (y 2,2–2,4) en was hij onzichtbaar; nu staat hij erop, onder het
 * scanvlak van de lidar. Bij de L-MATIC zat hij op het groene statuslampje;
 * nu staat hij naast de navigatielaser.
 */
const BAKEN: Record<TruckModel, [number, number, number]> = {
  'T-Matic': [0.22, 1.941, -0.632],
  'K-Matic': [0, 2.407, -1.07],
  'R-Matic': [-0.33, 2.4, -0.97],
  'L-Matic': [0.16, 1.89, -0.69],
}
const BLUESPOT_Z: Record<TruckModel, number> = {
  'T-Matic': 1.9,
  'K-Matic': 3.7,
  'R-Matic': 2.9,
  'L-Matic': 2.4,
}
/** Voetafdruk van de contactschaduw per model: breedte, lengte en verschuiving langs z. */
const SCHADUW: Record<TruckModel, { breedte: number; lengte: number; z: number }> = {
  'T-Matic': { breedte: 0.85, lengte: 2.0, z: 0 },
  'K-Matic': { breedte: 1.5, lengte: 3.2, z: -0.05 },
  'R-Matic': { breedte: 1.3, lengte: 2.4, z: -0.2 },
  'L-Matic': { breedte: 0.9, lengte: 1.8, z: 0 },
}

/** Bluespot bij achteruitrijden: achter de aandrijfkop. */
const BLUESPOT_ACHTER = -2.7
/**
 * Waar de K-Matic in het gangpad rijdt, uit het midden naar +z. Hij is een
 * smalle-gangentruck: hij rijdt vlak langs de stelling (23 cm speling, zoals
 * aan een geleiderail) en zet zijn vorken dwars. In het midden van ons brede
 * gangpad kwamen ze er nooit bij.
 *
 * De +z-kant en niet de −z-kant: de hotspotcamera staat aan −z in het
 * gangpad (`hotspotStand`), en een machine van 6 m in zijn zichtlijn zet hem
 * met zijn neus tegen de kap. Aan +z ziet hij de truck schuin van opzij, met
 * de vorken die de rechter stelling in gaan.
 */
const K_BAAN = 0.75

// Hoe de K- en R-MATIC hun werkcyclus rijden — met de snelheden van het
// typeblad — staat in `tempo.ts` (`WERKTUIG`).

/** De drie pallets die elke truck rondrijdt: elk een andere lading. */
const PALLETS: Partial<Record<TruckModel, { soort: Soort; n: number }[]>> = {
  'R-Matic': [{ soort: 'gewikkeld', n: 31 }, { soort: 'dozen', n: 32 }, { soort: 'kist', n: 33 }],
  'K-Matic': [{ soort: 'dozen', n: 41 }, { soort: 'vaten', n: 42 }, { soort: 'bigbag', n: 43 }],
}

export interface MaticProps {
  model: TruckModel
  tokens: MerkTokens
  /** z-positie van het gangpad waarin hij rijdt. */
  gangpad: number
  /** Midden van zijn werkgang op de x-as — normaal de hotspot waar hij bij hoort. */
  anker: number
  /** Halve lengte van die werkgang. */
  bereik: number
  snelheid: number
  fase: number
  richting?: 1 | -1
  /** K- en R-MATIC: de vakken waar hij pallets haalt en brengt. */
  werk?: Werkpunten
}

export function Matic(props: MaticProps) {
  const linde = useLindeBeeld()
  if (props.model === 'T-Matic' || props.model === 'L-Matic') return <HeenEnWeerMatic {...props} />
  if (!props.werk || !WERKTUIG[props.model]) return <RijdendeMatic {...props} />
  return linde ? <LindeWerkgang {...props} werk={props.werk} /> : <WerkendeMatic {...props} werk={props.werk} />
}

/* ------------------------------------------------------------------ zwaailicht en Bluespot */

/**
 * De vlek van de Bluespot: een zachte radiale val, gedeeld door alle trucks.
 * Een harde schijf met een rand leest als een sticker op de vloer, niet als
 * licht dat erop valt.
 */
let bluespotTextuur: THREE.DataTexture | null = null
function vlek(): THREE.DataTexture {
  if (bluespotTextuur) return bluespotTextuur
  const n = 64
  const data = new Uint8Array(n * n * 4)
  for (let y = 0; y < n; y++) {
    for (let x = 0; x < n; x++) {
      const r = Math.hypot((x + 0.5) / n - 0.5, (y + 0.5) / n - 0.5) * 2
      const a = Math.exp(-4.5 * r * r) * Math.max(0, 1 - r)
      const i = (y * n + x) * 4
      data[i] = data[i + 1] = data[i + 2] = 255
      data[i + 3] = Math.round(a * 255)
    }
  }
  bluespotTextuur = new THREE.DataTexture(data, n, n)
  bluespotTextuur.needsUpdate = true
  return bluespotTextuur
}

function Bakens({ model, baken, bluespot, lamp }: {
  model: TruckModel
  baken: RefObject<THREE.Mesh>
  bluespot: RefObject<THREE.Mesh>
  /** De lamp zelf, om hem te verbergen als de truck een Linde-beeld is: dat heeft er al een. */
  lamp?: RefObject<THREE.Group>
}) {
  const kaart = useMemo(vlek, [])
  return (
    <>
      {/* Zwaailicht: hoort bij de machine, niet bij het model — het staat per
          model ergens anders, want de T-Matic heeft geen mast om het op te zetten.
          Een zwarte voet met een amberen kap, zoals een led-flitser: geen
          gloeiende bol meer die op en neer pulseerde. */}
      <group ref={lamp} position={BAKEN[model]}>
        <mesh position={[0, 0.011, 0]}>
          <cylinderGeometry args={[0.034, 0.034, 0.022, 16]} />
          <meshStandardMaterial color="#141519" roughness={0.6} metalness={0.1} />
        </mesh>
        <mesh ref={baken} position={[0, 0.022, 0]}>
          <sphereGeometry args={[0.03, 16, 8, 0, Math.PI * 2, 0, Math.PI / 2]} />
          <meshStandardMaterial color="#c46a0c" emissive="#ff8a1a" emissiveIntensity={0.12} roughness={0.35} />
        </mesh>
      </group>

      {/* Bluespot: een blauwe vlek op de vloer, vóór de truck in de rijrichting
          — alleen zolang hij rijdt. */}
      <mesh ref={bluespot} position={[0, 0.02, BLUESPOT_Z[model]]} rotation={[-Math.PI / 2, 0, 0]} scale={[0.5, 0.8, 1]}
        renderOrder={4}>
        <planeGeometry args={[1, 1]} />
        <meshBasicMaterial
          map={kaart} color="#2f6bff" transparent opacity={0}
          blending={THREE.AdditiveBlending} depthWrite={false} toneMapped={false}
        />
      </mesh>
    </>
  )
}

/** 1,2 s, afgerond op een geheel aantal flitsen per omloop van de hal (`omloop.ts`). */
const FLITS = OMLOOP / Math.round(OMLOOP / 1.2)

/**
 * Dubbele flits elke 1,2 s, zoals een led-zwaailicht: twee korte pulsen met
 * een verval van 35 ms. Met een eigen fase per truck — eerst knipperden alle
 * vier precies tegelijk, en dat ziet een mens meteen als nep.
 */
function flits(tijd: number, fase: number) {
  const p = (((tijd + fase * FLITS) % FLITS) + FLITS) % FLITS
  const puls = (t0: number) => (p >= t0 ? Math.exp(-(p - t0) / 0.035) : 0)
  return Math.min(1, puls(0) + puls(0.16))
}

/** `rijdt`: hoe hard hij rijdt, 0 tot 1 — de Bluespot hoort bij een rijdende truck. */
function knipper(baken: THREE.Mesh | null, bluespot: THREE.Mesh | null, tijd: number, fase: number, aan: boolean, rijdt: number) {
  if (baken) {
    const m = baken.material as THREE.MeshStandardMaterial
    m.emissiveIntensity = aan ? 0.12 + 3.2 * flits(tijd, fase) : 0.12
  }
  if (bluespot) {
    const m = bluespot.material as THREE.MeshBasicMaterial
    m.opacity = 0.55 * THREE.MathUtils.clamp(rijdt, 0, 1)
  }
}

/* ------------------------------------------------------------------ K- en R-MATIC: halen en brengen */

/**
 * K- of R-MATIC die pallets uit de stelling haalt en elders wegzet, tussen
 * twee werkpunten (`werkcyclus.ts`). De drie pallets die hij rondrijdt staan
 * los in de scène: in hun vak als wereldobject, op de vorken als kind van de
 * vorkenwagen. Ze wisselen op het moment dat de vork stilstaat tegen de
 * pallet, en liggen dan precies op dezelfde plek.
 */
function WerkendeMatic({ model, gangpad, fase, werk }: MaticProps & { werk: Werkpunten }) {
  const w = WERKTUIG[model]!
  const pallets = PALLETS[model]!
  const groep = useRef<THREE.Group>(null)
  const bluespot = useRef<THREE.Mesh>(null)
  const baken = useRef<THREE.Mesh>(null)
  const brengt = useRef<THREE.Group>(null)
  const neemt = useRef<THREE.Group>(null)
  const vakken = useRef<(THREE.Group | null)[]>([])
  const anim = useAnimatie()
  const mat = useMaterialen()
  const kleuren: TruckKleuren = { rood: mat.truckRood, zwart: mat.truckZwart }

  const hef = useRef(0)
  const kant = useRef(1)
  const vork = useRef({ hoogte: w.rij, uit: 0 })
  // De klok van de hal (`omloop.ts`), niet elapsedTime × snelheid: met dat
  // product sprong elke truck naar een andere plek zodra iemand de
  // snelheidsschuif bewoog. Staan de trucks uit in de Studio, dan blijven ze
  // staan waar ze waren.
  const klok = useRef(0)

  const isK = model === 'K-Matic'
  // Van A naar B langs +x (zin 1) of −x. Hij staat met zijn vorken naar A.
  const zin = Math.sign(werk.b - werk.a) || 1
  // De K-MATIC is gespiegeld opgebouwd: zijn pallet op de vorken valt alleen
  // bij zin +1 samen met die in het vak. De indeling geeft nu altijd +1; gaat
  // dat ooit anders, dan liever een melding dan een pallet die omklapt.
  if (isK && zin !== 1) console.warn('[K-MATIC] werkgang van B naar A: de pallet op de vorken staat gespiegeld')
  // Oorsprong van de truck bij A en B: de R-MATIC met zijn draaipunt op het
  // vakmidden, de K-MATIC met zijn vorken erop — die liggen 2,05 m voor hem.
  const xa = werk.a + (isK ? K_VORK.midden * zin : 0)
  const xb = werk.b + (isK ? K_VORK.midden * zin : 0)
  const cyclus = useMemo(() => maakCyclus(w, Math.abs(xb - xa)), [w, xa, xb])

  // Het segment in de telling van `bezetting`. Een rerender per segment (een
  // halve minuut), niet per frame.
  const [k, zetK] = useState(0)
  const bez = useMemo(() => bezetting(k), [k])

  useFrame(() => {
    const g = groep.current
    if (!g) return
    if (anim.aan && anim.trucks) klok.current = halklok.t
    const tau = klok.current + fase * cyclus.paar
    const m = moment(w, cyclus, tau)
    const werkt = anim.mast
    // Zonder heffen (Studio) rijdt hij zijn rondje zonder pallets te verzetten.
    const kNu = werkt ? m.k : 0
    const achter = kNu !== k
    if (achter) zetK(kNu)

    const st = m.stand
    g.position.x = xa + (xb - xa) * m.ab
    if (isK) {
      g.position.z = gangpad + K_BAAN
      g.rotation.y = (-Math.PI / 2) * zin
    } else {
      // De R-MATIC draait om zijn lastwielas, niet om een punt 1,2 m achter
      // de truck: dat punt blijft liggen terwijl hij een kwartslag maakt. Met
      // zijn neus in de stelling (yaw 0) staat hij exact waar hij stond;
      // rijdend ligt zijn baan daardoor 0,61 m naar de stelling toe.
      const th = st.yaw * zin
      g.rotation.y = th
      g.position.x -= R_DRAAIPUNT * Math.sin(th)
      g.position.z = gangpad + st.naar + R_DRAAIPUNT * (1 - Math.cos(th))
    }
    // Lokaal +x wijst bij −π/2 naar wereld +z: de stelling waarin hij werkt.
    kant.current = g.rotation.y > 0 ? -1 : 1
    vork.current.hoogte = werkt ? st.hoogte : w.rij
    vork.current.uit = werkt ? st.uit : 0

    // Tot React het nieuwe segment heeft getekend, dragen de pallets de
    // nummers van het vorige. Dat is halverwege een rit, waar niets aan de
    // pallets verandert: dan laten we staan wat er stond.
    if (!achter) {
      const stand = werkt ? st : { ...st, gezet: false, gepakt: false }
      for (const wp of [0, 1]) {
        for (const j of [0, 1]) {
          const v = vakken.current[wp * 2 + j]
          if (v) v.visible = bez.vakken[wp][j].zichtbaar(stand)
        }
      }
      if (brengt.current) brengt.current.visible = werkt && !stand.gezet
      if (neemt.current) neemt.current.visible = werkt && stand.gepakt
    }

    if (bluespot.current) {
      // Hij waarschuwt voor een rijdende truck, in de richting waarin hij
      // rijdt: naar A vorken voorop, naar B achteruit. Stilstaand geen vlek;
      // de R-MATIC zou met zijn neus in de stelling achter de stelling op de
      // vloer projecteren.
      bluespot.current.position.z = m.rijrichting > 0 ? BLUESPOT_ACHTER : BLUESPOT_Z[model]
    }
    knipper(baken.current, bluespot.current, klok.current, fase, anim.bakens, Math.abs(m.snelheid) / 0.4)
  })

  // De pallet op de vorken, in het assenstelsel van de vorkenwagen: zo dat
  // hij met de vorken helemaal uit midden in het vak ligt.
  const opVork = isK
    ? {
        positie: [STELLING_OFFSET - K_BAAN - K_VORK.schuif, K_VORK.rust + palletOpVork(K_VORK.blad), K_VORK.midden] as const,
        // 1200 langs de truck (lokaal z), 800 langs de vorken (lokaal x).
        rotatie: [0, Math.PI / 2, 0] as const,
      }
    : {
        positie: [0, R_VORK.rust + palletOpVork(R_VORK.blad), STELLING_OFFSET - w.naar - R_VORK.reach] as const,
        rotatie: [0, 0, 0] as const,
      }
  const lading = (
    <group position={[...opVork.positie]} rotation={[...opVork.rotatie]}>
      <group ref={brengt} visible={false}><PalletMetLading {...pallets[bez.brengt]} /></group>
      <group ref={neemt} visible={false}><PalletMetLading {...pallets[bez.neemt]} /></group>
    </group>
  )

  return (
    <>
      <group ref={groep} name={model}>
        {isK
          ? <KMatic kleuren={kleuren} hef={hef} kant={kant} stand={vork} lading={lading} />
          : <RMatic kleuren={kleuren} hef={hef} stand={vork} lading={lading} />}
        <Bakens model={model} baken={baken} bluespot={bluespot} />
        <Contactschaduw {...SCHADUW[model]} />
      </group>

      {[0, 1].map((wp) => [0, 1].map((j) => (
        <group
          key={`${wp}${j}`}
          ref={(el) => { vakken.current[wp * 2 + j] = el }}
          position={[wp === 0 ? werk.a : werk.b, palletY(j), werk.stelling]}
          visible={false}
        >
          <PalletMetLading {...pallets[bez.vakken[wp][j].pallet]} />
        </group>
      )))}
    </>
  )
}

/* ------------------------------------------------------------------ K- en R-MATIC als Linde-beeld */

/**
 * De K- of R-MATIC als studiobeeld van Linde (`LindeBeeld`, 21 oktober). Een
 * render heeft één vaste mast- en vorkstand, dus hij haalt en brengt niets:
 * hij rijdt tussen zijn twee werkpunten heen en weer (`maakVasteRit`) in de
 * stand waarin Linde hem renderde — vorken omlaag en ingetrokken, de R-MATIC
 * met de mast van de draaitafel (`R_MAST.linde`). De K-MATIC draagt een
 * pallet op zijn vorken; de R-MATIC rijdt leeg, want met de mast ingetrokken
 * en de vorken omlaag ligt een pallet op zijn poten.
 *
 * Het 3D-model blijft als dieptestand-in in de groep; de stellingvakken staan
 * dan vol (`Magazijn`), want er wordt niets gehaald.
 */
function LindeWerkgang({ model, gangpad, fase, werk }: MaticProps & { werk: Werkpunten }) {
  const w = WERKTUIG[model]!
  const isK = model === 'K-Matic'
  const zin = Math.sign(werk.b - werk.a) || 1
  const xa = werk.a + (isK ? K_VORK.midden * zin : 0)
  const xb = werk.b + (isK ? K_VORK.midden * zin : 0)
  const rit = useMemo(() => maakVasteRit(w.rijden, xa, xb), [w, xa, xb])
  const groep = useRef<THREE.Group>(null)
  const model3d = useRef<THREE.Group>(null)
  const actief = useRef(false)
  const lamp = useRef<THREE.Group>(null)
  const bluespot = useRef<THREE.Mesh>(null)
  const baken = useRef<THREE.Mesh>(null)
  const anim = useAnimatie()
  const mat = useMaterialen()
  const kleuren: TruckKleuren = { rood: mat.truckRood, zwart: mat.truckZwart }
  const hef = useRef(0)
  const kant = useRef(1)
  const vork = useRef({ hoogte: isK ? K_VORK.rust : R_VORK.rust, uit: 0 })
  const klok = useRef(0)

  useFrame(() => {
    const g = groep.current
    if (!g) return
    if (anim.aan && anim.trucks) klok.current = halklok.t
    const t = momentHeenEnWeer(rit, klok.current + fase * rit.duur)
    // Dezelfde plek in het gangpad als rijdend in `WerkendeMatic`.
    const th = (-Math.PI / 2) * zin
    g.rotation.y = th
    if (isK) g.position.set(t.x, 0, gangpad + K_BAAN)
    else g.position.set(t.x - R_DRAAIPUNT * Math.sin(th), 0, gangpad + R_DRAAIPUNT * (1 - Math.cos(th)))
    // Naar B (verder van A) rijdt hij achteruit: de vlek achter de aandrijving.
    if (bluespot.current) bluespot.current.position.z = t.vx * zin > 0 ? BLUESPOT_ACHTER : BLUESPOT_Z[model]
    knipper(baken.current, bluespot.current, klok.current, fase, anim.bakens, Math.abs(t.vx) / 0.4)
    if (lamp.current) lamp.current.visible = !actief.current
  })

  // Zoals in `WerkendeMatic`: in het stelsel van de vorkenwagen, met de
  // vorken ingetrokken.
  const lading = isK
    ? (
      <group
        position={[STELLING_OFFSET - K_BAAN - K_VORK.schuif, K_VORK.rust + palletOpVork(K_VORK.blad), K_VORK.midden]}
        rotation={[0, Math.PI / 2, 0]} userData={{ lading: true }}
      >
        <PalletMetLading {...PALLETS['K-Matic']![0]} />
      </group>
    )
    : undefined

  return (
    <>
      <group ref={groep} name={model}>
        <group ref={model3d}>
          {isK
            ? <KMatic kleuren={kleuren} hef={hef} kant={kant} stand={vork} lading={lading} />
            : <RMatic kleuren={kleuren} hef={hef} stand={vork} mast={R_MAST.linde} />}
        </group>
        <Bakens model={model} baken={baken} bluespot={bluespot} lamp={lamp} />
        <Contactschaduw {...SCHADUW[model]} />
      </group>
      <LindeBeeld bron={LINDE_BEELD[isK ? 'K-Matic' : 'R-Matic']} drager={groep} model={model3d} actief={actief} />
    </>
  )
}

/* ------------------------------------------------------------------ T- en L-MATIC: heen en weer */

/** De pallet die de T- en de L-MATIC heen en weer brengen: elk een eigen lading. */
const HEEN_EN_WEER_PALLET: Record<'T-Matic' | 'L-Matic', { soort: Soort; n: number }> = {
  'T-Matic': { soort: 'gewikkeld', n: 51 },
  'L-Matic': { soort: 'dozen', n: 52 },
}

/**
 * T- of L-MATIC met één pallet tussen twee stations (`heenEnWeer.ts`). Drie
 * exemplaren van dezelfde pallet — op de vorken, op het kopstation, op het
 * staartstation — waarvan er precies één zichtbaar is. Ze wisselen op het
 * moment dat truck en vork stilstaan, en liggen dan per constructie op
 * dezelfde plek; net als bij de K- en R-MATIC is er geen rerender voor nodig.
 */
function HeenEnWeerMatic({ model, gangpad, anker, bereik, fase }: MaticProps) {
  const m = model as 'T-Matic' | 'L-Matic'
  const linde = useLindeBeeld()
  // Als Linde-beeld heft de L-MATIC niet: het overslagstation wordt een
  // vloerplek, zoals bij de T-MATIC. Sinds 22 oktober rijdt ook de T-MATIC
  // als Linde-beeld: de draaitafel van de T-MATIC core (`lindeKaarten.ts`).
  const cfg = useMemo(() => (linde && m === 'L-Matic' ? { ...HEEN_EN_WEER[m], kopHoogte: 0 } : HEEN_EN_WEER[m]), [linde, m])
  const lading = HEEN_EN_WEER_PALLET[m]
  // Op de omloop in de hal van de film: die van het Linde-beeld (`omloop.ts`).
  const h = useMemo(() => maakHeenEnWeer(cfg, anker, bereik, linde), [cfg, anker, bereik, linde])
  const groep = useRef<THREE.Group>(null)
  const model3d = useRef<THREE.Group>(null)
  const actief = useRef(false)
  const lamp = useRef<THREE.Group>(null)
  const bluespot = useRef<THREE.Mesh>(null)
  const baken = useRef<THREE.Mesh>(null)
  const opVork = useRef<THREE.Group>(null)
  const opKop = useRef<THREE.Group>(null)
  const opStaart = useRef<THREE.Group>(null)
  const anim = useAnimatie()
  const mat = useMaterialen()
  const kleuren: TruckKleuren = { rood: mat.truckRood, zwart: mat.truckZwart }
  // `hef` blijft voor de modellen bestaan (showroom); hier stuurt `vork`.
  const hef = useRef(0)
  const vork = useRef({ hoogte: cfg.rij })
  const klok = useRef(0)
  // Vorken in de rijrichting: de truck draait nooit om.
  const yaw = (cfg.zin * Math.PI) / 2

  useFrame(() => {
    const g = groep.current
    if (!g) return
    if (anim.aan && anim.trucks) klok.current = halklok.t
    const t = momentHeenEnWeer(h, klok.current + fase * h.duur)
    g.position.set(t.x, 0, gangpad)
    g.rotation.y = yaw
    // Zonder heffen (Studio) rijdt hij zijn rondje met de pallet op de vorken.
    const werkt = anim.mast
    vork.current.hoogte = werkt ? t.hoogte : cfg.rij
    const waar = werkt ? t.pallet : 'vork'
    if (opVork.current) opVork.current.visible = waar === 'vork'
    if (opKop.current) opKop.current.visible = waar === 'kop'
    if (opStaart.current) opStaart.current.visible = waar === 'staart'

    if (bluespot.current) {
      // Vorken voorop: de vlek vóór de vorken; achteruit: achter de aandrijving.
      bluespot.current.position.z = t.vx * cfg.zin >= 0 ? BLUESPOT_Z[model] : BLUESPOT_ACHTER
    }
    knipper(baken.current, bluespot.current, klok.current, fase, anim.bakens, Math.abs(t.vx) / 0.4)
    if (lamp.current) lamp.current.visible = !actief.current
  })

  // De pallet op de vorken: met zijn lange zijde langs de truck, de
  // dwarsplanken op het blad. Op een station dezelfde draaiing in de wereld,
  // anders klapt de lading bij het overpakken een halve slag om.
  const opDeVorken = (
    <group ref={opVork} position={[0, cfg.vorkTop - DWARSPLANK, cfg.p]} rotation={[0, Math.PI / 2, 0]} userData={{ lading: true }}>
      <PalletMetLading {...lading} />
    </group>
  )
  const draaiing: [number, number, number] = [0, yaw + Math.PI / 2, 0]

  return (
    <>
      <group ref={groep} name={model}>
        <group ref={model3d}>
          {model === 'T-Matic'
            ? <TMatic kleuren={kleuren} hef={hef} stand={vork} lading={opDeVorken} />
            : <LMatic kleuren={kleuren} hef={hef} stand={vork} lading={opDeVorken} />}
        </group>
        <Bakens model={model} baken={baken} bluespot={bluespot} lamp={lamp} />
        <Contactschaduw {...SCHADUW[model]} />
      </group>
      {linde && <LindeBeeld bron={LINDE_BEELD[m]} drager={groep} model={model3d} actief={actief} />}

      {/* Een pallet ligt met zijn lange zijde langs zijn eigen x: de schaduw dus ook.
          Als Linde-beeld na het beeld getekend: een neergezette pallet ligt
          ervoor zolang de vorken er nog in zitten. */}
      <group ref={opKop} position={[h.kopX, h.kopHoogte, gangpad]} rotation={draaiing} visible={false}>
        <Volgorde n={linde ? 11 : 0}><PalletMetLading {...lading} /></Volgorde>
        {h.kopHoogte === 0 && <Contactschaduw breedte={1.25} lengte={0.85} sterkte={0.5} />}
      </group>
      <group ref={opStaart} position={[h.staartX, 0, gangpad]} rotation={draaiing} visible={false}>
        <Volgorde n={linde ? 11 : 0}><PalletMetLading {...lading} /></Volgorde>
        <Contactschaduw breedte={1.25} lengte={0.85} sterkte={0.5} />
      </group>

      {h.kopHoogte > 0
        ? <Overslagstation x={h.kopX} z={gangpad} hoogte={h.kopHoogte} />
        : <Vloerkader x={h.kopX} z={gangpad} />}
      <Vloerkader x={h.staartX} z={gangpad} />
    </>
  )
}

/* ------------------------------------------------------------------ terugval */

/**
 * Een K- of R-MATIC waarvoor de indeling geen werkvakken vond (een kleine hal
 * in de Studio): hij rijdt zijn werkgang heen en weer en heft aan het eind.
 * Hij draait daarbij niet meer in één beeld om, maar rijdt achteruit terug.
 */
function RijdendeMatic({
  model, gangpad, anker, bereik, snelheid, fase, richting = 1,
}: MaticProps) {
  const groep = useRef<THREE.Group>(null)
  const bluespot = useRef<THREE.Mesh>(null)
  const baken = useRef<THREE.Mesh>(null)
  const anim = useAnimatie()
  const mat = useMaterialen()
  // hef in een ref: een setState per frame forceert een rerender per frame.
  const hef = useRef(0)
  const vorigeX = useRef<number | null>(null)
  const klok = useRef(0)

  const kleuren: TruckKleuren = { rood: mat.truckRood, zwart: mat.truckZwart }

  useFrame((_, delta) => {
    const g = groep.current
    if (!g) return
    if (anim.aan && anim.trucks) klok.current = halklok.t
    const t = (klok.current * snelheid + fase) % 1

    const heen = t < 0.5
    const lokaal = heen ? t / 0.5 : (1 - t) / 0.5

    // Rijden tot 78% van de halve cyclus, dan stilstaan en werken.
    const rijTot = 0.78
    const rij = Math.min(1, lokaal / rijTot)
    const soepel = rij < 0.5 ? 2 * rij * rij : 1 - Math.pow(-2 * rij + 2, 2) / 2

    g.position.x = anker + (soepel - 0.5) * 2 * bereik * richting
    g.position.z = gangpad
    g.rotation.y = richting === 1 ? Math.PI / 2 : -Math.PI / 2

    // Heffen in de stilstaande fase
    hef.current = anim.mast && lokaal > rijTot ? Math.sin(((lokaal - rijTot) / (1 - rijTot)) * Math.PI) : 0

    // Hoe hard hij rijdt, uit de verplaatsing van dit beeld: de Bluespot
    // hoort alleen bij een rijdende truck, en staat aan de kant waar hij heen rijdt.
    const dx = vorigeX.current === null ? 0 : g.position.x - vorigeX.current
    const v = delta > 0 ? Math.abs(dx) / delta : 0
    vorigeX.current = g.position.x
    if (bluespot.current) bluespot.current.position.z = dx * richting >= 0 ? BLUESPOT_Z[model] : BLUESPOT_ACHTER
    knipper(baken.current, bluespot.current, klok.current, fase, anim.bakens, v / 0.4)
  })

  return (
    <group ref={groep} name={model}>
      {model === 'R-Matic' && <RMatic kleuren={kleuren} hef={hef} />}
      {model === 'K-Matic' && <KMatic kleuren={kleuren} hef={hef} />}
      <Bakens model={model} baken={baken} bluespot={bluespot} />
      <Contactschaduw {...SCHADUW[model]} />
    </group>
  )
}
